import React from 'react';
import {
  AppWindowIcon,
  Avatar,
  BrickWallIcon,
  Button,
  ConstructionIcon,
  DownloadIcon,
  HammerIcon,
  PaintbrushIcon,
  PlugZapIcon,
  Tooltip,
  WrenchIcon,
} from '@billda/design-system';
import {
  TRADE_TEMPLATES,
  TradeTemplate,
} from '../../../constants/tradeTemplates';

const iconMap: Record<string, React.ReactElement> = {
  Construction: <ConstructionIcon />,
  Carpenter: <HammerIcon />,
  Plumbing: <WrenchIcon />,
  ElectricalServices: <PlugZapIcon />,
  Foundation: <BrickWallIcon />,
  Window: <AppWindowIcon />,
  FormatPaint: <PaintbrushIcon />,
};

interface TradeTemplatePickerProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (template: TradeTemplate) => void;
  siteName?: string;
}

const TradeTemplatePicker: React.FC<TradeTemplatePickerProps> = ({
  selectedTemplateId,
  onSelectTemplate,
  siteName,
}) => {
  const handleDownload = (e: React.MouseEvent, template: TradeTemplate) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = template.csvPath;
    link.download = template.fileName;
    link.click();
  };

  return (
    <div className="grid grid-cols-2 gap-4" data-testid="trade-template-picker">
      {TRADE_TEMPLATES.map(template => {
        const isSelected = selectedTemplateId === template.id;

        return (
          <div
            key={template.id}
            onClick={() => onSelectTemplate(template)}
            className={`relative cursor-pointer rounded-3xl border-2 p-5 transition-all duration-150 ${
              isSelected
                ? 'border-border-focused bg-surface-primary-soft'
                : 'border-transparent bg-surface-default hover:border-border-default'
            }`}
            data-testid={`template-${template.id}`}
          >
            <div className="flex items-start justify-between">
              <Avatar
                className="mb-3 size-10"
                style={{ backgroundColor: template.color }}
              >
                <span className="text-on-inverse">
                  {iconMap[template.icon] || <ConstructionIcon />}
                </span>
              </Avatar>
              <Tooltip content={`Download ${template.name} template`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-mt-1 -mr-1 h-7 w-7 p-0"
                  aria-label={`Download ${template.name} template`}
                  onClick={e => handleDownload(e, template)}
                >
                  <DownloadIcon className="size-4" aria-hidden="true" />
                </Button>
              </Tooltip>
            </div>

            <div className="type-label font-semibold text-content-primary">
              {template.name}
            </div>

            <div className="type-caption text-content-secondary">
              {template.labourItems.length} items &middot;{' '}
              {siteName ? `Preview for "${siteName}"` : '10 plots'}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TradeTemplatePicker;
